import api, { handleApiResponse, handleApiError } from './api';

class NotificationService {
  // Get notifications with pagination
  async getNotifications(params = {}) {
    try {
      const response = await api.get('/notifications', { params });
      return handleApiResponse(response);
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  }

  // Get unread notifications count
  async getUnreadCount() {
    try {
      const response = await api.get('/notifications/unread-count');
      return handleApiResponse(response);
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  }

  // Mark notification as read
  async markAsRead(id) {
    try {
      const response = await api.patch(`/notifications/${id}/read`);
      return handleApiResponse(response);
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  }

  // Mark all notifications as read
  async markAllAsRead() {
    try {
      const response = await api.patch('/notifications/mark-all-read');
      return handleApiResponse(response);
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  }

  // Delete notification
  async deleteNotification(id) {
    try {
      const response = await api.delete(`/notifications/${id}`);
      return handleApiResponse(response);
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  }

  // Assign ticket
  async assignTicket(ticketId, agentId) {
    try {
      const response = await api.post(`/tickets/${ticketId}/assign`, {
        agent_id: agentId
      });
      return handleApiResponse(response);
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  }

  // Escalate ticket
  async escalateTicket(ticketId, escalateToId = null, reason = '') {
    try {
      const response = await api.post(`/tickets/${ticketId}/escalate`, {
        escalate_to_id: escalateToId,
        reason: reason
      });
      return handleApiResponse(response);
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  }

  // Forward ticket
  async forwardTicket(ticketId, forwardToId, reason = '') {
    try {
      const response = await api.post(`/tickets/${ticketId}/forward`, {
        forward_to_id: forwardToId,
        reason: reason
    });
      return handleApiResponse(response);
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  }

  // Get notification icon based on type
  getNotificationIcon(type) {
    const iconMap = {
      'App\\Notifications\\TicketCreatedNotification': 'Plus',
      'App\\Notifications\\TicketAssignedNotification': 'UserCheck',
      'App\\Notifications\\TicketEscalatedNotification': 'AlertTriangle',
      'App\\Notifications\\TicketForwardedNotification': 'Forward',
      'App\\Notifications\\TicketResolvedNotification': 'CheckCircle',
      'App\\Notifications\\TicketClosedNotification': 'CheckCircle2',
      'App\\Notifications\\TicketCommentedNotification': 'MessageSquare',
      'App\\Notifications\\TicketStatusUpdatedNotification': 'RefreshCw',
      'ticket_created': 'Plus',
      'ticket_assigned': 'UserCheck',
      'ticket_escalated': 'AlertTriangle',
      'ticket_forwarded': 'Forward',
      'ticket_resolved': 'CheckCircle',
      'ticket_closed': 'CheckCircle2',
      'ticket_commented': 'MessageSquare',
      'ticket_status_updated': 'RefreshCw',
      'new_comment': 'MessageSquare',
      'system': 'Settings',
      'reminder': 'Clock',
      'warning': 'AlertTriangle',
      'info': 'Info',
      'default': 'Bell'
    };
    return iconMap[type] || iconMap.default;
  }

  // Get notification color based on type
  getNotificationColor(type) {
    const colorMap = {
      'App\\Notifications\\TicketCreatedNotification': 'blue',
      'App\\Notifications\\TicketAssignedNotification': 'indigo',
      'App\\Notifications\\TicketEscalatedNotification': 'red',
      'App\\Notifications\\TicketForwardedNotification': 'purple',
      'App\\Notifications\\TicketResolvedNotification': 'green',
      'App\\Notifications\\TicketClosedNotification': 'gray',
      'App\\Notifications\\TicketCommentedNotification': 'blue',
      'App\\Notifications\\TicketStatusUpdatedNotification': 'orange',
      'ticket_created': 'blue',
      'ticket_assigned': 'indigo',
      'ticket_escalated': 'red',
      'ticket_forwarded': 'purple',
      'ticket_resolved': 'green',
      'ticket_closed': 'gray',
      'ticket_commented': 'blue',
      'ticket_status_updated': 'orange',
      'system': 'purple',
      'reminder': 'yellow',
      'warning': 'orange',
      'error': 'red',
      'success': 'green',
      'default': 'gray'
    };
    return colorMap[type] || colorMap.default;
  }

  // Format notification time
  formatNotificationTime(timestamp) {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInMinutes = Math.floor((now - date) / (1000 * 60));

    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`;
    if (diffInMinutes < 10080) return `${Math.floor(diffInMinutes / 1440)}d ago`;

    return date.toLocaleDateString();
  }

  // Get notification priority
  getNotificationPriority(notification) {
    if (!notification.data) return 'normal';
    
    const data = typeof notification.data === 'string' 
      ? JSON.parse(notification.data) 
      : notification.data;
    
    // Check for escalated notifications
    if (notification.type?.includes('Escalated') || notification.type?.includes('escalated')) {
      return 'high';
    }
    
    if (data.priority === 'critical') {
      return 'high';
    }
    
    if (data.priority === 'high' || notification.type?.includes('Assigned') || notification.type?.includes('assigned')) {
      return 'medium';
    }
    
    return 'normal';
  }

 // Format notification message
formatNotificationMessage(notification) {
    if (!notification.data) return notification.type || 'New notification';
    
    const data = typeof notification.data === 'string' 
        ? JSON.parse(notification.data) 
        : notification.data;
    
    // Use the message from notification data if available
    if (data.message) {
        // For chat notifications, enhance the message with proper ticket ID
        if (notification.type === 'App\\Notifications\\ChatMessageNotification') {
            const ticketId = data.ticket_display_id || data.ticket_custom_id || `#${data.ticket_id}`;
            return `${data.message} on ${ticketId}`;
        }
        return data.message;
    }
    
    // Fallback: Use display_id, custom_id, or ticket_id in that order
    const ticketDisplayId = data.ticket_display_id || data.ticket_custom_id || `#${data.ticket_id}`;
    
    // Default message based on type with proper ticket ID
    const messageMap = {
        'App\\Notifications\\TicketCreatedNotification': `New ticket created: ${ticketDisplayId} - ${data.ticket_title || 'Untitled'}`,
        'App\\Notifications\\TicketAssignedNotification': `Ticket assigned to you: ${ticketDisplayId} - ${data.ticket_title || 'Untitled'}`,
        'App\\Notifications\\TicketEscalatedNotification': `Ticket escalated: ${ticketDisplayId} - ${data.ticket_title || 'Untitled'}`,
        'App\\Notifications\\TicketForwardedNotification': `Ticket forwarded to you: ${ticketDisplayId} - ${data.ticket_title || 'Untitled'}`,
        'App\\Notifications\\TicketResolvedNotification': `Ticket resolved: ${ticketDisplayId} - ${data.ticket_title || 'Untitled'}`,
        'App\\Notifications\\TicketCommentedNotification': `New comment on: ${ticketDisplayId} - ${data.ticket_title || 'Untitled'}`,
        'App\\Notifications\\TicketStatusUpdatedNotification': `Ticket status updated: ${ticketDisplayId} - ${data.ticket_title || 'Untitled'}`,
    };
    
    return messageMap[notification.type] || notification.type || 'New notification';
}

  // Get notification action URL
  getNotificationActionUrl(notification) {
    if (!notification.data) return null;
    
    const data = typeof notification.data === 'string' 
      ? JSON.parse(notification.data) 
      : notification.data;
    
    if (data.ticket_id) {
      return `/tickets/${data.ticket_id}`;
    }
    
    if (data.url) {
      return data.url;
    }
    
    return null;
  }

  // Check if notification is actionable
  isActionable(notification) {
    return !!this.getNotificationActionUrl(notification);
  }

  // Group notifications by date
  groupNotificationsByDate(notifications) {
    if (!Array.isArray(notifications)) return {};
    
    const groups = {};
    const today = new Date().toDateString();
    const yesterday = new Date(Date.now() - 86400000).toDateString();
    
    notifications.forEach(notification => {
      const notificationDate = new Date(notification.created_at).toDateString();
      let groupKey;
      
      if (notificationDate === today) {
        groupKey = 'Today';
      } else if (notificationDate === yesterday) {
        groupKey = 'Yesterday';
      } else {
        groupKey = new Date(notification.created_at).toLocaleDateString('en-US', {
          weekday: 'long',
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        });
      }
      
      if (!groups[groupKey]) {
        groups[groupKey] = [];
      }
      
      groups[groupKey].push(notification);
    });
    
    return groups;
  }

  // Filter notifications by type
  filterNotificationsByType(notifications, types) {
    if (!Array.isArray(notifications) || !Array.isArray(types)) {
      return notifications;
    }
    
    return notifications.filter(notification => 
      types.includes(notification.type)
    );
  }

  // Get notification types for filtering
  getNotificationTypes() {
    return [
      { value: 'App\\Notifications\\TicketCreatedNotification', label: 'Ticket Created', color: 'blue' },
      { value: 'App\\Notifications\\TicketAssignedNotification', label: 'Ticket Assigned', color: 'indigo' },
      { value: 'App\\Notifications\\TicketEscalatedNotification', label: 'Ticket Escalated', color: 'red' },
      { value: 'App\\Notifications\\TicketForwardedNotification', label: 'Ticket Forwarded', color: 'purple' },
      { value: 'App\\Notifications\\TicketResolvedNotification', label: 'Ticket Resolved', color: 'green' },
      { value: 'App\\Notifications\\TicketCommentedNotification', label: 'New Comment', color: 'blue' },
      { value: 'App\\Notifications\\TicketStatusUpdatedNotification', label: 'Status Updated', color: 'orange' }
    ];
  }

  // Extract simple notification type from full class name
  getSimpleNotificationType(fullType) {
    if (!fullType) return 'unknown';
    
    const typeMap = {
      'App\\Notifications\\TicketCreatedNotification': 'ticket_created',
      'App\\Notifications\\TicketAssignedNotification': 'ticket_assigned',
      'App\\Notifications\\TicketEscalatedNotification': 'ticket_escalated',
      'App\\Notifications\\TicketForwardedNotification': 'ticket_forwarded',
      'App\\Notifications\\TicketResolvedNotification': 'ticket_resolved',
      'App\\Notifications\\TicketClosedNotification': 'ticket_closed',
      'App\\Notifications\\TicketCommentedNotification': 'ticket_commented',
      'App\\Notifications\\TicketStatusUpdatedNotification': 'ticket_status_updated'
    };
    
    return typeMap[fullType] || fullType;
  }

  // Process WebSocket notification data
 processWebSocketNotification(notification) {
    // If custom_id is missing from notification data, we might need to fetch it
    const data = typeof notification.data === 'string' 
        ? JSON.parse(notification.data) 
        : notification.data;
    
    // Enhance data with display ID if missing
    if (data && data.ticket_id && !data.ticket_custom_id) {
        // You might want to fetch this from your backend or store it when notifications are created
        data.ticket_display_id = `#${data.ticket_id}`; // Fallback for now
    }
    
    return {
        id: notification.id,
        type: notification.type,
        notifiable_type: notification.notifiable_type,
        notifiable_id: notification.notifiable_id,
        data: data,
        read_at: notification.read_at,
        created_at: notification.created_at,
        updated_at: notification.updated_at,
        // Add computed properties
        simpleType: this.getSimpleNotificationType(notification.type),
        message: this.formatNotificationMessage(notification),
        icon: this.getNotificationIcon(notification.type),
        color: this.getNotificationColor(notification.type),
        priority: this.getNotificationPriority(notification),
        actionUrl: this.getNotificationActionUrl(notification),
        timeAgo: this.formatNotificationTime(notification.created_at),
        isActionable: this.isActionable(notification)
    };
}
}

export default new NotificationService();